import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getDkimKey, updateDkimKey } from 'src/actions/dkimKeys';
import { showAlert } from 'src/actions/globalAlert';
import { Loading } from 'src/components';
import { ExternalLink, PageLink, SubduedLink } from 'src/components/links';
import { Button, Layout, Page, Panel, Stack, TextField } from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import { Form } from 'src/components/tracking';
import { selectDomain } from 'src/selectors/sendingDomains';
import { DETAILS_BASE_URL } from '../constants';
import Domains from './index';

const DKIM_DOCS_URL =
  'https://developers.sparkpost.com/api/sending-domains/#sending-domains-dkim-keys';

function EditDkimKeyPage({
  domain,
  dkimKey,
  getLoading,
  updateLoading,
  getDkimKey,
  updateDkimKey,
  showAlert
}) {
  const history = useHistory();
  const { id: domainId, keyId } = useParams();
  const subaccountId = domain?.subaccount_id;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      signing_domain: '',
      headers: ''
    }
  });

  useEffect(() => {
    if (domainId && keyId) {
      getDkimKey({ domain: domainId, keyId, subaccount: subaccountId });
    }
  }, [getDkimKey, domainId, keyId, subaccountId]);

  useEffect(() => {
    if (dkimKey) {
      reset({
        signing_domain: dkimKey.signing_domain || '',
        headers: dkimKey.headers || ''
      });
    }
  }, [dkimKey, reset]);

  const onSubmit = (data) => {
    const payload = {
      domain: domainId,
      keyId,
      subaccount: subaccountId
    };

    if (data.signing_domain) {
      payload.signing_domain = data.signing_domain;
    }

    if (data.headers) {
      payload.headers = data.headers;
    }

    return updateDkimKey(payload)
      .then(() => {
        showAlert({
          type: 'success',
          message: `DKIM key "${keyId}" updated successfully.`
        });
        history.push(`${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys/${keyId}`);
      })
      .catch((err) => {
        showAlert({
          type: 'error',
          message: 'Failed to update DKIM key',
          details: err.message
        });
      });
  };

  const detailsPath = `${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys/${keyId}`;

  if (getLoading) {
    return <Loading />;
  }

  return (
    <Domains.Container>
      <Page
        title={`Edit DKIM Key: ${keyId}`}
        breadcrumbAction={{
          content: keyId,
          as: PageLink,
          to: detailsPath
        }}
      >
        <Form onSubmit={handleSubmit(onSubmit)} id="edit-dkim-key-form">
          <Layout>
            <Layout.Section annotated>
              <Layout.SectionTitle>Key Settings</Layout.SectionTitle>

              <Stack>
                <SubduedText fontSize="200">
                  Update the signing domain or headers for this DKIM key. Note that changing the
                  signing domain may require updating your DNS records.
                </SubduedText>

                <SubduedLink as={ExternalLink} to={DKIM_DOCS_URL} fontSize="200">
                  DKIM Keys Documentation
                </SubduedLink>
              </Stack>
            </Layout.Section>

            <Layout.Section>
              <Panel>
                <Panel.Section>
                  <Stack>
                    <TextField
                      id="dkim-key-id"
                      label="Key ID"
                      value={keyId}
                      disabled
                      helpText="Key ID cannot be changed."
                    />

                    <TextField
                      id="dkim-signing-domain"
                      label="Signing Domain"
                      helpText="The domain to use in the DKIM signature."
                      placeholder={domainId}
                      disabled={updateLoading}
                      error={errors.signing_domain?.message}
                      {...register('signing_domain')}
                    />

                    <TextField
                      id="dkim-headers"
                      label="Headers"
                      helpText="Colon-separated list of headers to include in the DKIM signature."
                      disabled={updateLoading}
                      error={errors.headers?.message}
                      {...register('headers')}
                    />
                  </Stack>
                </Panel.Section>
              </Panel>
            </Layout.Section>
          </Layout>

          <Layout>
            <Layout.Section annotated />
            <Layout.Section>
              <Button variant="primary" type="submit" loading={updateLoading}>
                Update DKIM Key
              </Button>
            </Layout.Section>
          </Layout>
        </Form>
      </Page>
    </Domains.Container>
  );
}

const mapStateToProps = (state) => ({
  domain: selectDomain(state),
  dkimKey: state.dkimKeys.key,
  getLoading: state.dkimKeys.getLoading,
  updateLoading: state.dkimKeys.updateLoading
});

export default connect(mapStateToProps, {
  getDkimKey,
  updateDkimKey,
  showAlert
})(EditDkimKeyPage);
